/**
 * Editor for an app custom-component library entry (ADR-0022 · open registries
 * #2). Edits `name` / `description` / `code` (the same Monaco `CodeField` the
 * page builder uses for the inline `custom_component` node — JS/JSX with a
 * `model` ambient-types `.d.ts` + expand-to-modal). A live preview renders the
 * component in the SAME sandboxed iframe as the portal, against a tiny sample
 * `model`, so the author sees the real thing while editing.
 *
 * The reference is live: a save here propagates to every page that references
 * this component by id (`source="library"`).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Save } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { CodeField } from "@/components/apps/pageBuilder/CodeField";
import { CustomComponentFrame } from "@/components/portal/CustomComponentFrame";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageContainer, PageContent } from "@/components/ui/page";
import { appsApi } from "@/lib/api-apps";
import type { Scope } from "@/lib/pageExpr";
import type { PageNode } from "@/lib/pageTypes";

export function ComponentLibraryEditorPage() {
  const { t } = useTranslation();
  const { appId = "", componentId = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const componentQ = useQuery({
    queryKey: ["apps", appId, "custom-components", componentId],
    queryFn: () => appsApi.getCustomComponent(appId, componentId),
  });

  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [code, setCode] = React.useState("");
  const [dirty, setDirty] = React.useState(false);

  // Hydrate local state once the component loads (and on id change).
  React.useEffect(() => {
    if (componentQ.data) {
      setName(componentQ.data.name);
      setDescription(componentQ.data.description ?? "");
      setCode(componentQ.data.code);
      setDirty(false);
    }
  }, [componentQ.data]);

  const save = useMutation({
    mutationFn: () =>
      appsApi.updateCustomComponent(appId, componentId, {
        name: name.trim(),
        description: description.trim() || null,
        code,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["apps", appId, "custom-components"] });
      qc.invalidateQueries({ queryKey: ["apps", appId, "custom-components", componentId] });
      setDirty(false);
    },
  });

  // A throwaway node + empty scope so the preview renders the live `code` in the
  // real sandboxed frame (transpile mode), interactive, against a sample model.
  const previewNode: PageNode = {
    id: "preview",
    type: "custom_component",
    props: { source: "transpile", code, height: 320 },
    style: {},
    children: [],
  };

  return (
    <PageContainer>
      <div className="flex items-center justify-between gap-3 border-b border-border/60 px-6 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/apps/${appId}`)}>
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            {t("common.back", "Indietro")}
          </Button>
          <div>
            <h1 className="text-base font-semibold">{name || t("apps.component", "Componente")}</h1>
            <p className="text-xs text-muted-foreground">
              {t("apps.componentLibraryHint", "Componente riutilizzabile — referenziabile da più pagine")}
            </p>
          </div>
        </div>
        <Button
          disabled={!name.trim() || !dirty || save.isPending}
          onClick={() => save.mutate()}
        >
          <Save className="h-4 w-4" strokeWidth={1.5} />
          {save.isPending ? t("common.saving", "Salvataggio…") : t("common.save", "Salva")}
        </Button>
      </div>

      <PageContent>
        {componentQ.isError ? (
          <Alert tone="danger" title={t("apps.loadFailed", "Caricamento fallito")}>
            {(componentQ.error as Error)?.message ?? "Error"}
          </Alert>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Left: metadata + code */}
            <div className="space-y-4">
              <div className="space-y-1">
                <Label>{t("apps.componentName", "Nome")}</Label>
                <Input
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setDirty(true);
                  }}
                  placeholder="Gauge"
                />
              </div>
              <div className="space-y-1">
                <Label>{t("common.description", "Descrizione")}</Label>
                <Input
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    setDirty(true);
                  }}
                  placeholder={t("common.optional", "Opzionale")}
                />
              </div>
              <CodeField
                label={t("apps.componentCode", "Codice (React/JSX)")}
                hint={t(
                  "apps.componentCodeHint",
                  "Definisci `function Component({ model }) { … }`. `model` è il binding bidirezionale (vars/queries/user + setVar/refresh/navigate/toast).",
                )}
                value={code}
                onChange={(v) => {
                  setCode(v);
                  setDirty(true);
                }}
              />
              {save.isError && (
                <Alert tone="danger" title={t("apps.saveFailed", "Salvataggio fallito")}>
                  {(save.error as Error)?.message ?? "Error"}
                </Alert>
              )}
            </div>

            {/* Right: live preview in the real sandboxed frame */}
            <div className="space-y-2">
              <Label>{t("apps.preview", "Anteprima")}</Label>
              {/* Fixed WHITE surface (not `bg-background`): the sandboxed frame
                  is transparent with dark text, so under the admin dark chrome it
                  would be dark-on-dark. White keeps it legible regardless of the
                  admin theme (and mirrors a default light portal surface). */}
              <div className="rounded-lg border border-border/60 bg-white p-3">
                <CustomComponentFrame
                  node={previewNode}
                  scope={{} as Scope}
                  rt={null}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t(
                  "apps.componentPreviewHint",
                  "Anteprima live nello stesso iframe sandboxato del portale, con un model di esempio.",
                )}
              </p>
            </div>
          </div>
        )}
      </PageContent>
    </PageContainer>
  );
}
